import {createAction} from '@reduxjs/toolkit'
import {Ticket} from '@wix/ambassador-wix-events-web/types'
import {
  ExtendedListScheduleItemsResponse,
  TicketWithFormattedSalePeriod,
  getEventId,
  isDraft,
  isTicketed,
} from '@wix/wix-events-commons-statics'
import {updateDates} from '../../../../commons/actions/dates'
import {DETAILS_ROUTE} from '../../../../commons/constants/navigation'
import {isFormViewState} from '../../../../commons/selectors/environment'
import {
  isEventsInMembersInstalled,
  isGroupsInstalled,
  isPaidPlansInstalled,
  isSchedulePageInstalled,
} from '../../../../commons/selectors/installed-apps'
import {getEvent as getEventSelector, isDemoEvent} from '../selectors/event'
import {createAsyncAction} from '../services/redux-toolkit'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {getDraftToken} from '../utils/query'
import {getCheckoutOptions} from './checkout-options'
import {getConnectedGroup} from './groups'
import {navigate} from './navigation'
import {getPlanList} from './paid-plans'
import {getMemberRsvp} from './rsvp'

export const changeEventAction = createAction<{
  event: wix.events.Event
  tickets: Ticket[] | TicketWithFormattedSalePeriod[]
  schedule: ExtendedListScheduleItemsResponse
}>('CHANGE_EVENT')

export const changeEvent =
  (slug: string) =>
  async (dispatch: Function, getState: GetState, {wixCodeApi, serverApi, flowAPI}: StoreExtraArgs) => {
    const state = getState()
    const responsive = flowAPI.environment.isEditorX
    const draftPreviewToken = getDraftToken(wixCodeApi)
    const schedulePageInstalled = isSchedulePageInstalled(state)
    const formViewState = isFormViewState(state)

    const {event, dates, tickets, schedule} = await serverApi.getData({
      slug,
      responsive,
      schedulePageInstalled,
      draftPreviewToken,
      flowAPI,
    })
    await dispatch(updateDates(dates))
    await dispatch(changeEventAction({event, tickets, schedule}))

    dispatch(getMemberRsvp())
    dispatch(getExtraEventData())
    dispatch(navigate({route: formViewState ? DETAILS_ROUTE.FORM : DETAILS_ROUTE.DETAILS}))
  }

export const getExtraEventData =
  () =>
  (dispatch: Function, getState: GetState): Promise<any>[] => {
    const state = getState()
    const event = getEventSelector(state)
    const promises = []

    if (event && !isDraft(event)) {
      promises.push(dispatch(getMembers()))

      if (isPaidPlansInstalled(state)) {
        promises.push(dispatch(getPlanList()))
      }

      if (isTicketed(event)) {
        promises.push(dispatch(getCheckoutOptions()))
      }

      if (isGroupsInstalled(state)) {
        promises.push(dispatch(getConnectedGroup()))
      }
    }

    return promises
  }

export const getEvent = createAsyncAction('GET_EVENT', async (_, {getState, extra: {serverApi}}) => {
  const state = getState()
  const event = getEventSelector(state)

  if (!isDemoEvent(state)) {
    return serverApi.getEvent(getEventId(event))
  }

  return event
})

export const getMembers = createAsyncAction('GET_MEMBERS', async (_, {getState, extra: {flowAPI, serverApi}}) => {
  const state = getState()
  const eventId = getEventSelector(state).id
  const {membersVisible = true} = flowAPI.controllerConfig?.config?.style?.styleParams?.booleans ?? {}

  if (membersVisible && isEventsInMembersInstalled(state) && eventId && !isDemoEvent(state)) {
    return serverApi.getMembers(eventId)
  }
})

export const shareEvent = createAction<{buttonName: string; origin: string}>('SHARE_EVENT')
